import { useEffect, useRef, useState } from 'react';

interface DelayedVisibilityOptions {
    showDelayMs?: number;
    minVisibleMs?: number;
}

// Anti-flicker gate for loading indicators: activity shorter than showDelayMs never
// shows the indicator, and once shown it stays up for at least minVisibleMs — which
// also bridges brief gaps between consecutive activity windows.
export const useDelayedVisibility = (
    active: boolean,
    { showDelayMs = 300, minVisibleMs = 750 }: DelayedVisibilityOptions = {}
): boolean => {
    const [visible, setVisible] = useState(false);
    const shownAtRef = useRef(0);

    useEffect(() => {
        if (active && !visible) {
            const timer = setTimeout(() => {
                shownAtRef.current = Date.now();
                setVisible(true);
            }, showDelayMs);
            return () => clearTimeout(timer);
        }
        if (!active && visible) {
            const remainingMs = Math.max(0, shownAtRef.current + minVisibleMs - Date.now());
            const timer = setTimeout(() => setVisible(false), remainingMs);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [active, visible, showDelayMs, minVisibleMs]);

    return visible;
};
